import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Callout } from './Callout';
import { Disclosure } from './Disclosure';
import { S } from '../strings';
import { Button } from './Button';
import './ErrorMessage.css';

export interface ErrorMessageProps {
  /** Swedish sentence saying what went wrong. */
  message: string;
  /** The next step: "Ta en ny bild eller fyll i själv." */
  next?: string;
  /** Raw error for the "Tekniska detaljer" disclosure. */
  error?: unknown;
  actions?: ReactNode;
  className?: string;
}

export function technicalDetails(error: unknown): string {
  if (!error) return '';
  if (error instanceof Error) return `${error.name}: ${error.message}${error.stack ? `\n${error.stack}` : ''}`;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

/** Error as a Swedish sentence with a next step and a technical-details disclosure. */
export function ErrorMessage({ message, next, error, actions, className }: ErrorMessageProps) {
  const details = technicalDetails(error);
  return (
    <Callout kind="danger" className={className} actions={actions}>
      <p>
        {message}
        {next ? ` ${next}` : ''}
      </p>
      {details ? (
        <Disclosure label={S.technicalDetails} size="s">
          <pre className="error-details">{details}</pre>
        </Disclosure>
      ) : null}
    </Callout>
  );
}

interface BoundaryState {
  error: unknown;
}

/** Route-level boundary so one broken page never takes the shell down. */
export class ErrorBoundary extends Component<{ children: ReactNode; fallbackTitle?: string }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Never log secrets; errors here are UI errors.
    console.error('UI error', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="error-boundary">
          <ErrorMessage
            message={this.props.fallbackTitle ?? 'Något gick fel på den här sidan.'}
            next="Ladda om sidan. Din bokföring är sparad lokalt."
            error={this.state.error}
            actions={
              <Button variant="outline" size="s" onClick={() => window.location.reload()}>
                Ladda om
              </Button>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}
